import { IonBackButton, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonContent, IonFooter, IonGrid, IonHeader, IonInput, IonItem, IonLabel, IonList, IonModal, IonPage, IonRow, IonSelect, IonSelectOption, IonSpinner, IonTitle, IonToolbar, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';

import './Pump.css';
import { useEffect, useState, useRef } from 'react';
import { AxiosResponse } from 'axios';
import getApi from '../../api';
import EnvironmentBadge from '../../components/EnvironmentBadge';
import { useParams } from 'react-router';
import Swal from 'sweetalert2';

const VLTParameters: React.FC = () => {
    const { id, userId } = useParams<{ id: string, userId: string }>();
    const [pump, setPump] = useState([] as any);
    const [isSaving, setIsSaving] = useState(false);
    const [editValues, setEditValues] = useState<{ [key: string]: string }>({});
    const [initialValues, setInitialValues] = useState<{ [key: string]: string }>({});
    const [showGuide, setShowGuide] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const guideSteps = [
        'language',
        'motor_speed_unit',
        'motor_power_kw',
        'motor_voltage',
        'motor_frequency',
        'motor_current',
        'motor_nominal_speed',
        'minimum_reference',
        'maximum_reference',
        'ramp_1_ramp_up_time',
        'ramp_1_ramp_down_time',
        'motor_speed_low_limit_hz',
        'motor_speed_high_limit_hz',
        'current_limit',
        'function_relay',
        'terminal_54_low_voltage',
        'terminal_54_high_voltage',
        'terminal_54_low_ref_feedb',
        'terminal_54_high_ref_feedb'
    ];

    const relayOptions = [
        { value: 0, label: 'No operation', desc: 'Reläet är inaktivt.' },
        { value: 1, label: 'Control ready', desc: 'Styrkortet är redo, men nätspänning kan saknas.' },
        { value: 2, label: 'Drive ready', desc: 'Omriktaren är klar för drift (nätspänning OK).' },
        { value: 5, label: 'Running', desc: 'Motorn körs.' },
        { value: 9, label: 'Alarm', desc: 'Reläet aktiveras vid ett larm (trip).' },
        { value: 10, label: 'Alarm or warning', desc: 'Aktiveras vid både larm och varningar.' },
        { value: 11, label: 'At torque limit', desc: 'Motorn har nått sin momentgräns.' },
        { value: 165, label: 'On reference', desc: 'Motorn har nått det inställda börvärdet.' },
        { value: 167, label: 'Start cmd. active', desc: 'Startkommando är aktivt (även om motorn ej roterar än).' },
        { value: 190, label: 'No alarm', desc: 'Reläet är aktivt så länge inget larm finns (felsäkert läge).' },
    ];

    const languageOptions = [
        { value: 0, label: 'English (Standard)' },
        { value: 1, label: 'Deutsch' },
        { value: 2, label: 'Français' },
        { value: 3, label: 'Dansk' },
        { value: 4, label: 'Español' },
        { value: 5, label: 'Italiano' },
        { value: 6, label: 'Svenska' },
        { value: 7, label: 'Nederlands' },
        { value: 10, label: 'Chinese' },
        { value: 20, label: 'Suomi' },
        { value: 22, label: 'Japanese' },
        { value: 23, label: 'Turkish' },
        { value: 24, label: 'Czech' },
        { value: 25, label: 'Polski' },
        { value: 26, label: 'Russian' },
        { value: 27, label: 'Thai' },
        { value: 28, label: 'Bahasa Indonesia' },
    ];

    const mainMenuAccessOptions = [
        { value: 0, label: 'Inget lösenord' },
        { value: 1, label: 'Bara läsa' },
        { value: 2, label: 'Kräv lösenord' },
    ];

    const editableParams = [
        'motor_power_kw', 'motor_voltage', 'motor_frequency', 'motor_current', 'motor_nominal_speed',
        'minimum_reference', 'maximum_reference', 'motor_speed_low_limit_hz', 'motor_speed_high_limit_hz',
        'current_limit', 'ramp_1_ramp_up_time', 'ramp_1_ramp_down_time', 'initial_ramp_time',
        'pressure_setpoint', 'no_flow_delay', 'pipe_fill_setpoint', 'pipe_fill_time', 'pipe_fill_hz',
        'terminal_32_digital_input', 'function_relay', 'terminal_54_low_voltage', 'terminal_54_high_voltage',
        'terminal_54_low_ref_feedb', 'terminal_54_high_ref_feedb', 'language', 'motor_speed_unit',
        'main_menu_access', 'main_menu_password'
    ];

    const isParameterChanged = (key: string) => {
        if (!editableParams.includes(key)) return false;

        const val1 = editValues[key];
        const val2 = initialValues[key];

        if (val1 === val2) return false;
        if (val1 === undefined || val2 === undefined) return val1 !== val2;

        const s1 = val1.toString().trim();
        const s2 = val2.toString().trim();

        if (s1 === s2) return false;

        // Try numeric comparison
        const n1 = parseFloat(s1.replace(',', '.'));
        const n2 = parseFloat(s2.replace(',', '.'));

        if (!isNaN(n1) && !isNaN(n2)) {
            return n1 !== n2;
        }

        return s1 !== s2;
    };

    const fetchData = async (isInitial = false) => {
        getApi().get(`/bbe/userpumps/${id}/`).then((response: AxiosResponse) => {
            setPump(response.data);
            const status = response.data.danfoss_status;
            if (status) {
                const values: { [key: string]: string } = {};
                Object.keys(status).forEach(key => {
                    values[key] = status[key]?.toString() || '';
                });
                if (isInitial) {
                    setEditValues(values);
                    setInitialValues(values);
                } else {
                    // Only update values that haven't been edited by the user
                    setEditValues(prev => {
                        const newValues = { ...prev };
                        Object.keys(values).forEach(key => {
                            if (prev[key] === initialValues[key]) {
                                newValues[key] = values[key];
                            }
                        });
                        return newValues;
                    });
                    setInitialValues(values);
                }
            }
        });
    }

    const handleSave = async () => {
        const changes: any[] = [];

        editableParams.forEach(key => {
            if (isParameterChanged(key)) {
                const val = editValues[key];
                // Try to parse as number if it looks like one, otherwise keep as string
                const parsedValue = (val === '' || isNaN(Number(val))) ? val : Number(val);

                changes.push({
                    method: "setValue",
                    TagName: key,
                    TagValue: parsedValue
                });
            }
        });

        if (changes.length === 0) {
            Swal.fire({
                title: 'Inga ändringar',
                text: 'Du har inte ändrat några värden.',
                icon: 'info',
                timer: 1500,
                showConfirmButton: false,
                heightAuto: false
            });
            return;
        }

        const result = await Swal.fire({
            title: `Spara ändringar?`,
            text: `Vill du spara ${changes.length} ändringar?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Ja, spara',
            cancelButtonText: 'Avbryt',
            heightAuto: false
        });

        if (result.isConfirmed) {
            setIsSaving(true);
            try {
                // The user specified the JSON format: { pump_id: X, changes: [...] }
                // Based on context, pump_id should be danfoss_status.user_pump
                const payload = {
                    pump_id: pump.danfoss_status?.user_pump || pump.id,
                    changes: changes
                };

                await getApi().post(`/bbe/update-parameters/`, payload);

                Swal.fire({
                    title: 'Skickat!',
                    text: 'Ändringarna har skickats till frekvensaren. Det kan ta några sekunder innan värdena bekräftas.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    heightAuto: false
                });

                // Don't reset initialValues or call fetchData(true) here. 
                // Let the background fetch naturally confirm when the server has updated.
                fetchData();
            } catch (error) {
                Swal.fire({
                    title: 'Fel!',
                    text: 'Kunde inte spara ändringarna.',
                    icon: 'error',
                    heightAuto: false
                });
            } finally {
                setIsSaving(false);
            }
        }
    };

    const getLabel = (key: string) => {
        const labels: { [key: string]: string } = {
            motor_power_kw: 'Effekt (kW)',
            motor_voltage: 'Spänning (V)',
            motor_frequency: 'Frekvens (Hz)',
            motor_current: 'Ström (A)',
            motor_nominal_speed: 'Nominellt varvtal (RPM)',
            minimum_reference: 'Min referens (Hz)',
            maximum_reference: 'Max referens (Hz)',
            ramp_1_ramp_up_time: 'Ramp upp tid (s)',
            ramp_1_ramp_down_time: 'Ramp ner tid (s)',
            initial_ramp_time: 'Initial ramp tid (s)',
            motor_speed_low_limit_hz: 'Låg gräns frekvens (Hz)',
            motor_speed_high_limit_hz: 'Hög gräns frekvens (Hz)',
            current_limit: 'Strömgräns (%)',
            terminal_32_digital_input: 'Terminal 32 Digital ingång',
            function_relay: 'Funktion relä',
            terminal_54_low_voltage: 'Terminal 54 Låg spänning (V)',
            terminal_54_high_voltage: 'Terminal 54 Hög spänning (V)',
            terminal_54_low_ref_feedb: 'Terminal 54 Låg ref feedback',
            terminal_54_high_ref_feedb: 'Terminal 54 Hög ref feedback',
            actual_frequency: 'Aktiv frekvens (Hz)',
            actual_current: 'Aktiv ström (A)',
            actual_power_kw: 'Aktiv effekt (kW)',
            kwh_counter: 'kWh räknare',
            status_word: 'Statusord',
            alarm_word: 'Larmord',
            warning_word: 'Varningsord',
            pressure_setpoint: 'Börvärde tryck (bar)',
            no_flow_delay: 'Ingen flödesfördröjning (s)',
            pipe_fill_setpoint: 'Rörfyllning börvärde (bar)',
            pipe_fill_time: 'Rörfyllningstid (s)',
            pipe_fill_hz: 'Rörfyllning frekvens (Hz)',
            language: 'Språk',
            motor_speed_unit: 'Hastighetsenhet motor',
            main_menu_access: 'Huvudmeny åtkomst',
            main_menu_password: 'Huvudmeny lösenord',
        };
        return labels[key] || key;
    };

    useIonViewWillEnter(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        fetchData(true);
        intervalRef.current = setInterval(() => {
            fetchData();
        }, 10000);
    });

    useIonViewWillLeave(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    });

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const hasChanges = editableParams.some(key => isParameterChanged(key));

    const renderParameterItem = (key: string, type: "number" | "text" = "number", readonly = false) => {
        const isRelay = key === 'function_relay';
        const isMainMenuAccess = key === 'main_menu_access';
        const changed = isParameterChanged(key);
        // Use live status for readonly items to ensure they update in background
        const displayValue = readonly ? (pump.danfoss_status?.[key] ?? '') : (editValues[key] ?? '');

        return (
            <IonItem key={key} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel position="stacked" style={{ fontWeight: 'bold', color: 'var(--ion-color-step-600)' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    {getLabel(key)}
                </IonLabel>
                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    {isRelay || isMainMenuAccess ? (
                        <IonSelect
                            value={parseInt(editValues[key] || '0')}
                            onIonChange={(e: any) => setEditValues(prev => ({ ...prev, [key]: e.detail.value.toString() }))}
                            placeholder="Välj alternativ"
                            style={{ width: '100%', color: changed ? 'var(--ion-color-primary)' : 'inherit', fontWeight: changed ? 'bold' : 'normal' }}
                            onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                        >
                            {(isRelay ? relayOptions : mainMenuAccessOptions).map(opt => (
                                <IonSelectOption key={opt.value} value={opt.value} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{opt.label}</IonSelectOption>
                            ))}
                        </IonSelect>
                    ) : (
                        <IonInput
                            type={type}
                            value={displayValue}
                            readonly={readonly}
                            onIonChange={(e: any) => setEditValues(prev => ({ ...prev, [key]: e.detail.value! }))}
                            placeholder={`Ange ${getLabel(key)}`}
                            onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                            style={{ color: changed ? 'var(--ion-color-primary)' : 'inherit', fontWeight: changed ? 'bold' : 'normal' }}
                        />
                    )}
                </div>
                {isRelay && (
                    <div slot="helper" style={{ fontSize: '0.8em', color: 'var(--ion-color-step-500)', padding: '4px 0' }}>
                        {relayOptions.find(o => o.value === parseInt(editValues[key] || '0'))?.desc}
                    </div>
                )}
            </IonItem>
        );
    };

    return (
        <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonToolbar mode="md" color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonBackButton />
                    </IonButtons>
                    <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>VLT parametrar - {pump['name']}</IonTitle>
                    <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonButton
                            fill="outline"
                            color="light"
                            onClick={() => { setCurrentStep(0); setShowGuide(true); }}
                            style={{ '--border-radius': '8px', margin: '0 8px' }}
                            placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                        >
                            Starta Guide
                        </IonButton>
                        <IonButton
                            fill="solid"
                            color="light"
                            onClick={handleSave}
                            disabled={!hasChanges || isSaving}
                            style={{ '--border-radius': '8px', fontWeight: 'bold', margin: '0 8px' }}
                            placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                        >
                            {isSaving ? <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /> : 'Spara ändringar'}
                        </IonButton>
                        <div style={{ paddingRight: '8px' }}><EnvironmentBadge /></div>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonGrid placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>

                        {/* Motordata */}
                        <IonCol size="12" size-sm="6" size-md="4" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Motordata</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {renderParameterItem('motor_power_kw')}
                                        {renderParameterItem('motor_voltage')}
                                        {renderParameterItem('motor_frequency')}
                                        {renderParameterItem('motor_current')}
                                        {renderParameterItem('motor_nominal_speed')}
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* Gränsvärden & Ramper */}
                        <IonCol size="12" size-sm="6" size-md="4" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Gränsvärden & Ramper</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {renderParameterItem('minimum_reference')}
                                        {renderParameterItem('maximum_reference')}
                                        {renderParameterItem('motor_speed_low_limit_hz')}
                                        {renderParameterItem('motor_speed_high_limit_hz')}
                                        {renderParameterItem('current_limit')}
                                        {renderParameterItem('ramp_1_ramp_up_time')}
                                        {renderParameterItem('ramp_1_ramp_down_time')}
                                        {renderParameterItem('initial_ramp_time')}
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* Pump & Rörfyllning */}
                        <IonCol size="12" size-sm="6" size-md="4" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Pump & Rörfyllning</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {renderParameterItem('pressure_setpoint')}
                                        {renderParameterItem('no_flow_delay')}
                                        {renderParameterItem('pipe_fill_setpoint')}
                                        {renderParameterItem('pipe_fill_time')}
                                        {renderParameterItem('pipe_fill_hz')}
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* Driftstatus */}
                        <IonCol size="12" size-sm="6" size-md="4" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Driftstatus</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {renderParameterItem('actual_frequency', "number", true)}
                                        {renderParameterItem('actual_current', "number", true)}
                                        {renderParameterItem('actual_power_kw', "number", true)}
                                        {renderParameterItem('kwh_counter', "number", true)}
                                        {renderParameterItem('status_word', "number", true)}
                                        {renderParameterItem('alarm_word', "number", true)}
                                        {renderParameterItem('warning_word', "number", true)}
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* IO Inställningar */}
                        <IonCol size="12" size-sm="6" size-md="4" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>IO Inställningar</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {renderParameterItem('terminal_32_digital_input')}
                                        {renderParameterItem('function_relay')}
                                        {renderParameterItem('terminal_54_low_voltage')}
                                        {renderParameterItem('terminal_54_high_voltage')}
                                        {renderParameterItem('terminal_54_low_ref_feedb')}
                                        {renderParameterItem('terminal_54_high_ref_feedb')}
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {/* Systeminställningar */}
                        <IonCol size="12" size-sm="6" size-md="4" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Systeminställningar</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        {renderParameterItem('main_menu_access')}
                                        {renderParameterItem('main_menu_password')}
                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonLabel position="stacked" style={{ fontWeight: 'bold', color: 'var(--ion-color-step-600)' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Språk</IonLabel>
                                            <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                                <IonSelect
                                                    value={parseInt(editValues['language'] || '0')}
                                                    onIonChange={(e: any) => setEditValues(prev => ({ ...prev, language: e.detail.value.toString() }))}
                                                    placeholder="Välj språk"
                                                    style={{ width: '100%', color: editValues['language'] !== initialValues['language'] ? 'var(--ion-color-primary)' : 'inherit', fontWeight: editValues['language'] !== initialValues['language'] ? 'bold' : 'normal' }}
                                                    onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                >
                                                    {languageOptions.map(opt => (
                                                        <IonSelectOption key={opt.value} value={opt.value} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{opt.label}</IonSelectOption>
                                                    ))}
                                                </IonSelect>
                                            </div>
                                        </IonItem>

                                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                            <IonLabel position="stacked" style={{ fontWeight: 'bold', color: 'var(--ion-color-step-600)' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Hastighetsenhet motor</IonLabel>
                                            <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                                <IonSelect
                                                    value={parseInt(editValues['motor_speed_unit'] || '0')}
                                                    onIonChange={(e: any) => setEditValues(prev => ({ ...prev, motor_speed_unit: e.detail.value.toString() }))}
                                                    placeholder="Välj enhet"
                                                    style={{ width: '100%', color: editValues['motor_speed_unit'] !== initialValues['motor_speed_unit'] ? 'var(--ion-color-primary)' : 'inherit', fontWeight: editValues['motor_speed_unit'] !== initialValues['motor_speed_unit'] ? 'bold' : 'normal' }}
                                                    onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                                >
                                                    <IonSelectOption value={0} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>RPM (varv/min)</IonSelectOption>
                                                    <IonSelectOption value={1} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Hz (Hertz)</IonSelectOption>
                                                </IonSelect>
                                            </div>
                                            <div slot="helper" style={{ fontSize: '0.8em', color: 'var(--ion-color-step-500)', padding: '8px 0' }}>
                                                {editValues['motor_speed_unit'] === '1'
                                                    ? 'Alla varvtalsinställningar och visningar sker i frekvens (Hertz).'
                                                    : 'Alla varvtalsinställningar och visningar sker i varv per minut.'}
                                            </div>
                                        </IonItem>
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                    </IonRow>
                </IonGrid>
            </IonContent>

            <IonModal isOpen={showGuide} onDidDismiss={() => setShowGuide(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonToolbar mode="md" color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Guide: Steg {currentStep + 1} av {guideSteps.length}</IonTitle>
                        <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonButton onClick={() => setShowGuide(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Stäng</IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <h2 style={{ color: 'var(--ion-color-primary)' }}>{getLabel(guideSteps[currentStep])}</h2>
                        <p style={{ color: 'var(--ion-color-step-600)' }}>Justera värdet nedan och tryck på Nästa.</p>
                    </div>

                    <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            {guideSteps[currentStep] === 'language' ? (
                                <IonSelect
                                    key="guide-language"
                                    value={parseInt(editValues['language'] || '0')}
                                    onIonChange={(e: any) => {
                                        const newVal = e.detail.value?.toString() || '0';
                                        setEditValues(prev => ({ ...prev, language: newVal }));
                                    }}
                                    placeholder="Välj språk"
                                    style={{ width: '100%' }}
                                    onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                >
                                    {languageOptions.map(opt => (
                                        <IonSelectOption key={opt.value} value={opt.value} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{opt.label}</IonSelectOption>
                                    ))}
                                </IonSelect>
                            ) : guideSteps[currentStep] === 'motor_speed_unit' ? (
                                <IonSelect
                                    key="guide-motor-speed-unit"
                                    value={parseInt(editValues[guideSteps[currentStep]] || '0')}
                                    onIonChange={(e: any) => {
                                        const newVal = e.detail.value?.toString() || '0';
                                        setEditValues(prev => ({ ...prev, [guideSteps[currentStep]]: newVal }));
                                    }}
                                    placeholder="Välj enhet"
                                    style={{ width: '100%' }}
                                    onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                >
                                    <IonSelectOption value={0} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>RPM (varv/min)</IonSelectOption>
                                    <IonSelectOption value={1} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Hz (Hertz)</IonSelectOption>
                                </IonSelect>
                            ) : guideSteps[currentStep] === 'function_relay' ? (
                                <IonSelect
                                    key="guide-function-relay"
                                    value={parseInt(editValues[guideSteps[currentStep]] || '0')}
                                    onIonChange={(e: any) => {
                                        const newVal = e.detail.value?.toString() || '0';
                                        setEditValues(prev => ({ ...prev, [guideSteps[currentStep]]: newVal }));
                                    }}
                                    placeholder="Välj funktion"
                                    style={{ width: '100%' }}
                                    onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                >
                                    {relayOptions.map(opt => (
                                        <IonSelectOption key={opt.value} value={opt.value} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{opt.label}</IonSelectOption>
                                    ))}
                                </IonSelect>
                            ) : (
                                <IonInput
                                    key={`guide-input-${guideSteps[currentStep]}`}
                                    type="number"
                                    value={editValues[guideSteps[currentStep]]}
                                    onIonChange={(e: any) => setEditValues(prev => ({ ...prev, [guideSteps[currentStep]]: e.detail.value! }))}
                                    placeholder={`Ange ${getLabel(guideSteps[currentStep])}`}
                                    onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                    style={{ fontSize: '1.2em' }}
                                />
                            )}
                        </IonCardContent>
                    </IonCard>

                    {guideSteps[currentStep] === 'motor_speed_unit' && (
                        <p style={{ fontSize: '0.9em', color: 'var(--ion-color-step-600)', padding: '0 16px' }}>
                            {editValues['motor_speed_unit'] === '1'
                                ? 'Alla varvtalsinställningar och visningar sker i frekvens (Hertz).'
                                : 'Alla varvtalsinställningar och visningar sker i varv per minut.'}
                        </p>
                    )}

                    {guideSteps[currentStep] === 'function_relay' && (
                        <p style={{ fontSize: '0.9em', color: 'var(--ion-color-step-600)', padding: '0 16px' }}>
                            {relayOptions.find(o => o.value === parseInt(editValues['function_relay'] || '0'))?.desc}
                        </p>
                    )}
                </IonContent>
                <IonFooter placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonToolbar mode="md" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonButton disabled={currentStep === 0} onClick={() => setCurrentStep(prev => prev - 1)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Föregående</IonButton>
                        </IonButtons>
                        <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            {currentStep < guideSteps.length - 1 ? (
                                <IonButton color="primary" onClick={() => setCurrentStep(prev => prev + 1)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Nästa</IonButton>
                            ) : (
                                <IonButton color="success" onClick={() => setShowGuide(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Slutför</IonButton>
                            )}
                        </IonButtons>
                    </IonToolbar>
                </IonFooter>
            </IonModal>
        </IonPage>
    );
};

export default VLTParameters;
